import { ModeToggle } from "@/components/mode-toggle";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "@tanstack/react-router";
import { OAuthProvider } from "appwrite";
import { Bot, Mail, ArrowRight, Lock, EyeOffIcon, EyeIcon } from "lucide-react";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { toast } from "react-toastify";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "@/components/ui/input-otp";

gsap.registerPlugin(useGSAP, ScrollTrigger, ScrollToPlugin);

const Robot3dLazy = React.lazy(() =>
  import("@/components/models3d/cardModels3d/robot3D")
);

const Login = () => {
  const [formVisible, setFormVisible] = useState(false);

  const [otpStep, setOtpStep] = useState<"email" | "code">("email");
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpUserId, setOtpUserId] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const { user, loginUser, loginWithOAuth, sendEmailOTP, verifyEmailOTP } = useAuth();
  const loginForm = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showRobot, setShowRobot] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const [showPassword, setShowPassword] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);

  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const robotContainerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLDivElement | null)[]>([]);
  const formRef = useRef<HTMLDivElement>(null);
  const modeToggleRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (leftPanelRef.current) leftPanelRef.current.style.willChange = 'transform, opacity';
    if (rightPanelRef.current) rightPanelRef.current.style.willChange = 'transform, opacity';
    if (logoRef.current) logoRef.current.style.willChange = 'transform, opacity';

    const cleanup = setTimeout(() => {
      if (leftPanelRef.current) leftPanelRef.current.style.willChange = 'auto';
      if (rightPanelRef.current) rightPanelRef.current.style.willChange = 'auto';
      if (logoRef.current) logoRef.current.style.willChange = 'auto';
    }, 2500);

    return () => clearTimeout(cleanup);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const id = requestAnimationFrame(() => setShowRobot(true));
    return () => cancelAnimationFrame(id);
  }, [isMobile]);

  useEffect(() => {
    if (user) navigate({ to: "/dashboard" });
  }, [user]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const checkMobile = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setIsMobile(window.innerWidth < 1024);
      }, 150);
    };
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(timeout);
    };
  }, []);

  const handleMicrosoftLogin = () => {
    setIsOAuthLoading(true);
    setTimeout(() => { loginWithOAuth(OAuthProvider.Microsoft); }, 100);
  };

  useGSAP(() => {
    const ctx = gsap.context(() => {
      gsap.set([leftPanelRef.current, rightPanelRef.current], { clearProps: 'all' });

      const tl = gsap.timeline({ delay: 0.1 });

      if (isMobile) {
        tl.from(leftPanelRef.current, { opacity: 0, duration: 0.5, ease: 'power2.out' })
          .from(logoRef.current, { y: -20, opacity: 0, duration: 0.4, ease: 'power2.out' }, '-=0.2')
          .from(titleRef.current?.children || [], { y: 20, opacity: 0, stagger: 0.05, duration: 0.4, ease: 'power2.out' }, '-=0.2')
          .from(buttonRefs.current, { y: 15, opacity: 0, stagger: 0.08, duration: 0.4, ease: 'power2.out' }, '-=0.2')
          .from(modeToggleRef.current, { opacity: 0, duration: 0.3, ease: 'power2.out' }, '-=0.2');
      } else {
        // FIX: bloquear scroll del body durante la animación de entrada
        // para que los paneles entrando desde fuera no creen scrollbar temporal
        document.body.style.overflow = 'hidden';

        tl.from(leftPanelRef.current, { x: '-100%', duration: 1.2, ease: 'power3.inOut' })
          .from(rightPanelRef.current, { x: '100%', duration: 1.2, ease: 'power3.inOut' }, '<')
          .from(logoRef.current, { scale: 0, opacity: 0, rotation: -180, duration: 1, ease: 'elastic.out(1, 0.5)' }, '-=0.5')
          .from(titleRef.current?.children || [], { y: 100, opacity: 0, rotationX: -90, stagger: 0.05, duration: 0.8, ease: 'back.out(1.5)' }, '-=0.6')
          .from(buttonRefs.current, { x: -100, opacity: 0, stagger: 0.15, duration: 0.8, ease: 'power3.out' }, '-=0.5')
          .from(robotContainerRef.current, { opacity: 0, duration: 0.8, ease: 'power2.out' }, '-=1')
          .from(modeToggleRef.current, { scale: 0, rotation: 360, opacity: 0, duration: 0.6, ease: 'back.out(2)' }, '-=0.8')
          .call(() => { document.body.style.overflow = ''; }); // limpiar al terminar

        gsap.to(particlesRef.current?.children || [], {
          y: 'random(-100, 100)', x: 'random(-100, 100)',
          opacity: 'random(0.2, 0.8)', scale: 'random(0.5, 1.5)',
          duration: 'random(4, 8)', repeat: -1, yoyo: true, ease: 'sine.inOut',
          stagger: { amount: 3, from: 'random' }
        });
      }
    });

    return () => {
      // limpiar overflow si el componente se desmonta durante la animación
      document.body.style.overflow = '';
      ctx.revert();
    };
  }, [isMobile]);

  useGSAP(() => {
    if (!formRef.current) return;
    const formEl = formRef.current;
    const inputs = formEl.querySelectorAll('.form-field');

    if (formVisible) {
      const tl = gsap.timeline();
      tl.to(formEl, { maxHeight: 400, opacity: 1, duration: 0.4, ease: 'power2.out' })
        .from(inputs, { y: 20, opacity: 0, stagger: 0.08, duration: 0.3 }, '-=0.2');
      formEl.style.pointerEvents = 'auto';
    } else {
      gsap.to(formEl, {
        maxHeight: 0, opacity: 0, duration: 0.3, ease: 'power2.in',
        onComplete: () => { formEl.style.pointerEvents = 'none'; }
      });
    }
  }, [formVisible]);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSendingOTP) return;
    if (!otpEmail) { toast.warning("Ingresa tu correo electrónico"); return; }

    try {
      setIsSendingOTP(true);
      const { userId } = await sendEmailOTP(otpEmail);
      setOtpUserId(userId);
      setOtpStep("code");
      toast.success("¡Código enviado! Revisa tu correo");
    } catch (error: any) {
      if (error?.message === "email_not_found") {
        toast.error("Este correo no está registrado en el sistema");
      } else {
        toast.error("No se pudo enviar el código. Intenta de nuevo");
      }
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isVerifyingOTP) return;
    if (!otpCode) { toast.warning("Ingresa el código"); return; }

    try {
      setIsVerifyingOTP(true);
      await verifyEmailOTP(otpUserId, otpCode);
      setDialogOpen(false);
    } catch {
      toast.error("Código incorrecto o expirado");
      setOtpCode("");
    } finally {
      setIsVerifyingOTP(false);
    }
  };


  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // reset al cerrar
      setOtpStep("email");
      setOtpEmail("");
      setOtpCode("");
      setOtpUserId("");
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!loginForm.current) return;
    const email = (loginForm.current.elements.namedItem("email") as HTMLInputElement)?.value;
    const password = (loginForm.current.elements.namedItem("password") as HTMLInputElement)?.value;
    if (!email || !password) { toast.warning("Llena todos los campos"); return; }
    setIsLoading(true);
    try {
      await loginUser({ email, password });
    } catch {
      toast.error("Credenciales incorrectas");
      setIsLoading(false);
    }
  };

  return (
    // FIX: h-screen en lugar de min-h-screen — login es pantalla fija, sin scroll de página
    <div className="relative h-screen w-screen overflow-hidden font-outfit bg-[#f5f4f2] dark:bg-[#0e0e0e]">

      {/* ── Subtle grid ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)
          `,
          backgroundSize: '64px 64px',
          opacity: 0.5,
          maskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)',
        }}
      />
      {/* dark grid */}
      <div
        className="absolute inset-0 pointer-events-none hidden dark:block"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)',
        }}
      />

      {/* ── Ambient orbs ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-50 -left-37.5 w-150 h-150 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 65%)',
            filter: 'blur(60px)',
          }}
        />
        <div className="absolute -bottom-37.5 -right-25 w-125 h-125 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(14,165,233,0.06) 0%, transparent 65%)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      {/* ── Particles — desktop only ── */}
      {!isMobile && (
        <div ref={particlesRef} className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-0.5 h-0.5 rounded-full bg-neutral-400 dark:bg-neutral-600"
              style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
            />
          ))}
        </div>
      )}

      {/* ── Mode toggle ── */}
      <div ref={modeToggleRef} className="fixed top-8 right-8 z-50">
        <div className="relative group">
          <div className="absolute -inset-2 bg-neutral-200 dark:bg-neutral-800 blur-md opacity-0 group-hover:opacity-60 transition duration-300" />
          <div className="relative backdrop-blur-xl p-2">
            <ModeToggle />
          </div>
        </div>
      </div>

      {/* ── Layout ── */}
      {/* FIX: h-full para que herede el h-screen del padre exactamente */}
      <div className="relative h-full flex">

        {/* Left panel */}
        {/*
          FIX RESPONSIVE:
          - h-full: hereda altura exacta del padre (h-screen), sin crecer
          - overflow-y-auto: permite scroll interno si el contenido (form abierto) excede la altura
          - items-start lg:items-center: en desktop centra verticalmente; en pantallas cortas
            el contenido empieza desde arriba para que el scroll funcione correctamente
          - scrollbar oculta: overflow funciona pero la barra no se ve
        */}
        <div
          ref={leftPanelRef}
          className="w-full lg:w-1/2 relative flex items-start lg:items-center justify-center p-8 lg:p-16 h-full overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          {/* panel glass */}
          <div className="absolute inset-0 bg-white/30 dark:bg-black/20 lg:backdrop-blur-sm" />

          {/* Grid light mode */}
          <div
            className="absolute inset-0 pointer-events-none dark:hidden"
            style={{
              backgroundImage: `
                linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)
              `,
              backgroundSize: '64px 64px',
              opacity: 0.5,
            }}
          />

          {/* Grid dark mode */}
          <div
            className="absolute inset-0 pointer-events-none hidden dark:block"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
              `,
              backgroundSize: '64px 64px',
              opacity: 1,
            }}
          />

          {/* vertical divider */}
          <div className="hidden lg:block absolute top-0 right-0 h-full w-px bg-linear-to-b from-transparent via-neutral-300/60 dark:via-neutral-700/60 to-transparent" />

          {/*
            FIX: py-10 lg:py-0
            - En móvil/pantallas cortas: padding vertical para que el contenido
              no quede pegado al borde cuando hace scroll
            - En desktop: sin padding (centrado por items-center del padre)
          */}
          <div className="relative w-full max-w-md space-y-6 lg:space-y-8 py-10 lg:py-0">

            {/* Logo */}
            <div ref={logoRef} className="relative">
              <div className="absolute -inset-4 bg-neutral-100 dark:bg-neutral-900 rounded-2xl blur-2xl opacity-60" />
              <div className="relative">
                <img
                  src="/images/Logo-light.png"
                  className="block dark:hidden h-14 lg:h-14 w-auto"
                  alt="Logo"
                />
                <img
                  src="/images/Logo-dark.png"
                  className="hidden dark:block h-14 lg:h-14 w-auto"
                  alt="Logo"
                />
              </div>
            </div>

            {/* Title */}
            <div ref={titleRef} className="space-y-2 lg:space-y-3">
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold font-outfit">
                <span className="block text-neutral-800 dark:text-neutral-100">Bienvenido</span>
                <span className="block text-neutral-400 dark:text-neutral-500 font-light">
                  de vuelta
                </span>
              </h1>
              <p className="text-neutral-500 dark:text-neutral-500 text-base lg:text-lg font-nunito">
                Todo en un solo lugar
              </p>
            </div>

            {/* Buttons */}
            <div className="space-y-3 lg:space-y-4">

              {/* Microsoft */}
              <div ref={(el) => { buttonRefs.current[0] = el; }} className="group relative">
                <div className="hidden lg:block absolute -inset-1 bg-neutral-200 dark:bg-neutral-800 rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition duration-500" />
                <Button
                  className="relative w-full h-12 lg:h-14 flex items-center justify-between bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200 transition-all duration-300 rounded-xl font-medium text-sm lg:text-base cursor-pointer shadow-sm"
                  onClick={handleMicrosoftLogin}
                  disabled={isOAuthLoading}
                  // disabled={true}
                >
                  {isOAuthLoading ? (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
                        <span>Cargando…</span>
                      </div>
                      <div className="w-5" />
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <img src="/icons/microsoft.png" className="w-4 lg:w-5 h-4 lg:h-5" alt="" />
                        <span>Continuar con Microsoft</span>
                      </div>
                      <ArrowRight className="w-4 lg:w-5 h-4 lg:h-5 opacity-0 group-hover:opacity-60 transition-opacity" />
                    </>
                  )}
                </Button>
              </div>

              {/* Magic link */}
              <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                <DialogTrigger asChild>
                  <div ref={(el) => { buttonRefs.current[1] = el; }} className="group relative">
                    <div className="hidden lg:block absolute -inset-1 bg-neutral-300 dark:bg-neutral-700 rounded-2xl blur-lg opacity-0 group-hover:opacity-40 transition duration-500" />
                    <Button
                      className="relative w-full h-12 lg:h-14 bg-neutral-900 dark:bg-neutral-100 hover:bg-neutral-700 dark:hover:bg-white text-white dark:text-neutral-900 transition-all duration-300 rounded-xl font-medium text-sm lg:text-base shadow-md cursor-pointer"
                    >
                      <Mail className="w-4 lg:w-5 h-4 lg:h-5 mr-2 lg:mr-3" />
                      Continuar con código
                      <ArrowRight className="w-4 lg:w-5 h-4 lg:h-5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Button>
                  </div>
                </DialogTrigger>

                <DialogContent className="sm:max-w-md bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-800 text-neutral-900 dark:text-neutral-100">

                  {/* ── Paso 1: email ── */}
                  {otpStep === "email" && (
                    <form onSubmit={handleSendOTP}>
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-outfit">
                          Acceso por código
                        </DialogTitle>
                        <DialogDescription className="text-neutral-500">
                          Te enviaremos un código de 6 dígitos a tu correo
                        </DialogDescription>
                      </DialogHeader>

                      <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                          <Label htmlFor="otp-email" className="text-neutral-700 dark:text-neutral-300">
                            Email
                          </Label>
                          <Input
                            id="otp-email"
                            type="email"
                            placeholder="Ingresa tu correo"
                            value={otpEmail}
                            onChange={(e) => setOtpEmail(e.target.value)}
                            className="h-11 bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800"
                            required
                            autoFocus
                          />
                        </div>
                      </div>

                      <DialogFooter className="gap-2">
                        <DialogClose asChild>
                          <Button type="button" variant="outline" className="cursor-pointer">
                            Cancelar
                          </Button>
                        </DialogClose>
                        <Button
                          type="submit"
                          disabled={isSendingOTP}
                          className="bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 hover:bg-neutral-700 dark:hover:bg-white cursor-pointer"
                        >
                          {isSendingOTP ? (
                            <>
                              <div className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin mr-2" />
                              Enviando...
                            </>
                          ) : (
                            <>Enviar código</>
                          )}
                        </Button>
                      </DialogFooter>
                    </form>
                  )}

                  {/* ── Paso 2: código ── */}
                  {otpStep === "code" && (
                    <form onSubmit={handleVerifyOTP}>
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-outfit">
                          Ingresa el código
                        </DialogTitle>
                        <DialogDescription className="text-neutral-500">
                          Enviamos un código de 6 dígitos a{" "}
                          <span className="text-neutral-700 dark:text-neutral-300 font-medium">
                            {otpEmail}
                          </span>
                        </DialogDescription>
                      </DialogHeader>

                      <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                          <div className="grid gap-2">


                            <div className="flex justify-center py-2">
                              <InputOTP
                                maxLength={6}
                                value={otpCode}
                                onChange={(value) => setOtpCode(value)}
                                autoFocus
                              >
                                <InputOTPGroup>
                                  <InputOTPSlot index={0} className="h-12 w-12 text-lg bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800" />
                                  <InputOTPSlot index={1} className="h-12 w-12 text-lg bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800" />
                                  <InputOTPSlot index={2} className="h-12 w-12 text-lg bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800" />
                                </InputOTPGroup>
                                <InputOTPSeparator />
                                <InputOTPGroup>
                                  <InputOTPSlot index={3} className="h-12 w-12 text-lg bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800" />
                                  <InputOTPSlot index={4} className="h-12 w-12 text-lg bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800" />
                                  <InputOTPSlot index={5} className="h-12 w-12 text-lg bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800" />
                                </InputOTPGroup>
                              </InputOTP>
                            </div>
                          </div>
                        </div>

                        {/* volver a intentar */}
                        <button
                          type="button"
                          onClick={() => setOtpStep("email")}
                          className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 text-left transition-colors"
                        >
                          ¿No llegó el código? Volver a intentar
                        </button>
                      </div>

                      <DialogFooter className="gap-2">
                        <DialogClose asChild>
                          <Button type="button" variant="outline" className="cursor-pointer">
                            Cancelar
                          </Button>
                        </DialogClose>
                        <Button
                          type="submit"
                          disabled={isVerifyingOTP || otpCode.length < 6}
                          className="bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 hover:bg-neutral-700 dark:hover:bg-white cursor-pointer"
                        >
                          {isVerifyingOTP ? (
                            <>
                              <div className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin mr-2" />
                              Verificando...
                            </>
                          ) : (
                            <>Ingresar</>
                          )}
                        </Button>
                      </DialogFooter>
                    </form>
                  )}

                </DialogContent>
              </Dialog>

              {/* Divider */}
              <div className="relative py-3 lg:py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-200 dark:border-neutral-800" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 text-xs lg:text-sm text-neutral-400 dark:text-neutral-600 bg-[#f5f4f2] dark:bg-[#0e0e0e]">
                    o continúa con
                  </span>
                </div>
              </div>

              {/* Email/password toggle */}
              <div ref={(el) => { buttonRefs.current[2] = el; }} className="group relative">
                <div className="hidden lg:block absolute -inset-1 bg-neutral-200 dark:bg-neutral-800 rounded-2xl blur-lg opacity-0 group-hover:opacity-50 transition duration-500" />
                <Button
                  className="relative w-full h-12 lg:h-14 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 transition-all duration-300 rounded-xl font-medium text-sm lg:text-base cursor-pointer"
                  onClick={() => setFormVisible(!formVisible)}
                >
                  <Lock className="w-4 lg:w-5 h-4 lg:h-5 mr-2 lg:mr-3" />
                  Email y Contraseña
                  <div className={`ml-auto transition-transform duration-300 ${formVisible ? 'rotate-90' : ''}`}>
                    <ArrowRight className="w-4 lg:w-5 h-4 lg:h-5" />
                  </div>
                </Button>
              </div>
            </div>

            {/* Form */}
            <div ref={formRef} className="overflow-hidden" style={{ maxHeight: 0, opacity: 0, pointerEvents: 'none' }}>
              <div className="space-y-3 lg:space-y-4 pt-3 lg:pt-4">
                <form onSubmit={handleSubmit} ref={loginForm} className="space-y-3 lg:space-y-4">
                  <div className="form-field">
                    <Label htmlFor="email" className="text-neutral-700 dark:text-neutral-300 font-medium text-sm">Email</Label>
                    <Input
                      id="email" type="email" name="email"
                      placeholder="Ingresa tu correo.."
                      className="mt-1.5 h-11 lg:h-12 bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400"
                    />
                  </div>
                  <div className="form-field">
                    <Label className="text-neutral-700 dark:text-neutral-300 font-medium text-sm">Contraseña</Label>
                    <div className="relative mt-1.5">
                      <Input
                        type={showPassword ? "text" : "password"} name="password"
                        placeholder="Ingresa tu contraseña.."
                        className="h-11 lg:h-12 pr-10 bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-3 flex items-center text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                      >
                        {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                      </button>
                    </div>
                  </div>
                  <div className="form-field pt-2">
                    <Button
                      type="submit"
                      className="cursor-pointer w-full h-12 lg:h-14 bg-neutral-900 dark:bg-neutral-100 hover:bg-neutral-700 dark:hover:bg-white text-white dark:text-neutral-900 font-medium rounded-xl shadow-md"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <div className="w-4 lg:w-5 h-4 lg:h-5 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin mr-2" />
                          Ingresando...
                        </>
                      ) : (
                        <>Ingresar <ArrowRight className="w-4 lg:w-5 h-4 lg:h-5 ml-2" /></>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>

          </div>
        </div>

        {/* Right panel — robot */}
        <div ref={rightPanelRef} className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-16 h-full">

          <div className="absolute inset-0 bg-linear-to-bl from-neutral-100/60 via-neutral-50/30 to-transparent dark:from-neutral-900/60 dark:via-neutral-950/30 dark:to-transparent backdrop-blur-sm" />

          <div ref={robotContainerRef} className="relative">
            {/* Rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="absolute w-150 h-150 rounded-full border border-neutral-400/25 dark:border-neutral-500/20 animate-ping"
                style={{ animationDuration: '3s' }}
              />
              <div className="absolute w-125 h-125 rounded-full border border-neutral-400/18 dark:border-neutral-500/15" />
              <div className="absolute w-100 h-100 rounded-full border border-neutral-400/12 dark:border-neutral-500/10" />
            </div>
            {/* Glow */}
            <div className="absolute inset-0 bg-linear-to-r from-neutral-300/20 to-neutral-400/20 dark:from-neutral-700/20 dark:to-neutral-600/20 rounded-full blur-[100px]" />

            {/* 3D model */}
            <div className="relative w-112.5 h-112.5 flex items-center justify-center">
              {showRobot ? (
                <Suspense fallback={
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="relative">
                      <div className="w-24 h-24 border-4 border-neutral-200 dark:border-neutral-800 border-t-neutral-500 dark:border-t-neutral-400 rounded-full animate-spin" />
                      <Bot className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 text-neutral-400" />
                    </div>
                  </div>
                }>
                  <Robot3dLazy />
                </Suspense>
              ) : (
                <div className="w-full h-full" />
              )}
            </div>
          </div>
        </div>

      </div>

      <div
        className="loader-footer absolute bottom-8 flex items-center gap-3"
        style={{ color: 'var(--loader-dot)' }}>
        <div style={{ width: 16, height: 1, background: 'var(--loader-line)' }} />
        <span className="text-[10px] tracking-[0.35em] text-gray-500 font-outfit">V 1.0.0</span>
        <div style={{ width: 16, height: 1, background: 'var(--loader-line)' }} />
      </div>

    </div>
  );
};

export default Login;